/** Profile / plan payment lifecycle labels aligned with GET /api/profile/. */

export function paymentStatusMeta(user) {
  const payment = user?.current_plan_payment;
  const membership = String(user?.membership_status || '').toLowerCase();

  if (payment === 'rejected' || membership === 'rejected') {
    return {
      label: 'Payment rejected',
      tone: 'rejected',
      needsUpload: true,
      awaitingReview: false,
    };
  }
  if (payment === 'pending' || user?.payment_proof_pending) {
    return {
      label: 'Awaiting admin review',
      tone: 'pending',
      needsUpload: false,
      awaitingReview: true,
    };
  }
  if (membership === 'active' || payment === 'active') {
    return {
      label: 'Active',
      tone: 'active',
      needsUpload: false,
      awaitingReview: false,
    };
  }
  if (membership === 'pending') {
    return {
      label: 'Pending activation',
      tone: 'pending',
      needsUpload: true,
      awaitingReview: false,
    };
  }
  return {
    label: membership === 'none' || !membership ? 'Not joined' : membership,
    tone: 'inactive',
    needsUpload: true,
    awaitingReview: false,
  };
}

export function membershipBadgeClass(tone) {
  if (tone === 'active') {
    return 'bg-emerald-50 text-emerald-600 border-emerald-200';
  }
  if (tone === 'pending') {
    return 'bg-amber-50 text-amber-700 border-amber-200';
  }
  if (tone === 'rejected') {
    return 'bg-rose-50 text-rose-600 border-rose-200';
  }
  return 'bg-red-50 text-red-500 border-red-200';
}

/** Member-facing EMI payment window countdown (0 from API → "Open"). */
export function formatEmiWindowDisplay(
  daysRemaining,
  {
    isActive = true,
    overdueDays = 0,
    blockCountdown = null,
    windowStatus = null,
  } = {},
) {
  if (!isActive) {
    return { value: 'N/A', badge: 'No membership', hint: '', warning: null };
  }

  const overdue = Number(overdueDays ?? 0);
  if (Number.isFinite(overdue) && overdue > 0) {
    const hint =
      overdue === 1 ? '1 day overdue' : `${overdue} days overdue`;
    let warning = null;
    if (blockCountdown !== null && blockCountdown !== undefined) {
      const untilBlock = Number(blockCountdown);
      if (Number.isFinite(untilBlock)) {
        if (untilBlock <= 0) {
          warning =
            'Upload unavailable. Please contact Admin Support to enable payment uploads for your account.';
        } else if (untilBlock === 1) {
          warning =
            '⚠ Your payment upload will be disabled in 1 day if payment is not completed.';
        } else {
          warning = `⚠ Your payment upload will be disabled in ${untilBlock} days if payment is not completed.`;
        }
      }
    }
    return {
      value: 'Overdue',
      badge: 'Overdue',
      hint,
      warning,
    };
  }

  const days = Number(daysRemaining ?? 0);
  const status = String(windowStatus || '').toLowerCase();

  if (!Number.isFinite(days) || days <= 0) {
    return {
      value: 'Open',
      badge: 'Open',
      hint: status === 'first_month'
        ? 'Pay your first EMI anytime this month'
        : 'You can pay your EMI now',
      warning: null,
    };
  }

  if (status === 'before_window') {
    const opensLabel = days === 1 ? 'Opens in 1 day' : `Opens in ${days} days`;
    return {
      value: `${days}d`,
      badge: opensLabel,
      hint: 'Payment window opens on the 1st of the month',
      warning: null,
    };
  }

  const remainingLabel = days === 1 ? '1 day remaining' : `${days} days remaining`;
  return {
    value: `${days}d`,
    badge: remainingLabel,
    hint: 'Pay before the 15th deadline',
    warning: null,
  };
}
